import type { QualityTier, ScalerConfig } from '../config.js';
import { crawlRenderScale, higherTier, lowerTier, renderScaleFloor } from './tiers.js';

/**
 * Dynamic resolution and quality-tier governor, as a pure reducer over frame
 * times so it can be tested without a GPU.
 *
 * Every `windowMs` it looks at the average frame rate:
 * - **Too slow:** lower the render scale straight away, by roughly the amount
 *   the frame rate is short (GPU cost scales with pixel count, i.e. scale²),
 *   never below the tier's floor, and by at least `minCutStep`: each new
 *   scale reallocates the post-process targets, so a slow spell gets one
 *   decisive cut instead of a stall per small one (#501). That keeps 60 fps
 *   instead of letting it collapse, without ever going blurry.
 * - **Every cut and tier drop is a probe.** If the next window isn't faster,
 *   the GPU isn't (or is no longer) the bottleneck: the frame rate is capped
 *   (iOS Low Power Mode runs Safari at 30 fps) or the CPU is busy. Lowering
 *   quality further would only cost sharpness, so undo that step and hold at
 *   that frame rate until it changes. The hold is re-tested now and then, less
 *   often each time the cap is confirmed.
 * - **A cut that didn't help, far below target** (`tierProbeBelowFps`): the
 *   pixels aren't the cost, but the tier's effects may be. Put the pixels
 *   back and probe the next tier down first; only if that doesn't help
 *   either is it a cap (#501). A tier probe that failed blocks tier probes
 *   for `tierProbeCooldownMs`: until then a cap re-test at the floor changes
 *   nothing, it only measures.
 * - **Pinned at the floor and still well below target** for a while, with the
 *   last cut having helped: step the quality tier down (softer bloom, then none).
 * - **At target for a while:** raise resolution a step at a time, then the
 *   tier, never above the tier the player started on. Vsync caps the reading
 *   at 60 fps, so headroom is invisible; if a raise makes us drop again
 *   straight away, wait twice as long before the next try.
 *
 * The first `graceMs` are ignored (shader compiles), and so are the first
 * `settleWindows` windows after any change (resizing render targets causes a
 * hitch, and the frame rate takes a moment to settle after it).
 */
export interface GovernorState {
  readonly tier: QualityTier;
  /** The tier the player started on; recovery never goes above it. */
  readonly ceiling: QualityTier;
  /**
   * Fraction of the DPR-capped resolution we render at, in [floor, 1]; after
   * `crawl` it can sit below the tier's floor, at one render pixel per CSS
   * pixel, until the raises lift it.
   */
  readonly renderScale: number;
  readonly elapsedMs: number;
  readonly windowMs: number;
  readonly windowFrames: number;
  /** Windows still to discard after the last change. */
  readonly settleWindows: number;
  /** Continuous time at target frame rate (resolution raises). */
  readonly goodMs: number;
  readonly raiseAfterMs: number;
  /** Time since the last resolution raise, or null if the last change wasn't one. */
  readonly sinceRaiseMs: number | null;
  /** Set by a resolution cut; the next window tells whether that cut helped. */
  readonly probe: { readonly baselineFps: number; readonly scaleBefore: number } | null;
  /** Set by a tier drop; the next window tells whether the drop helped. */
  readonly tierProbe: { readonly baselineFps: number; readonly tierBefore: QualityTier } | null;
  /** Holding at a frame rate that lower quality can't fix. */
  readonly cap: { readonly fps: number; readonly heldMs: number; readonly holdMs: number } | null;
  /** How long the next cap hold lasts before a re-test; doubles each time one is confirmed. */
  readonly capHoldMs: number;
  /**
   * No tier probe before `elapsedMs` reaches this: a failed tier probe puts
   * it `tierProbeCooldownMs` ahead (#501).
   */
  readonly tierProbeAfterMs: number;
  /** Continuous time stuck at the floor and still too slow. */
  readonly slowAtFloorMs: number;
  /** Continuous time at target at full resolution (tier raises). */
  readonly tierGoodMs: number;
  readonly tierRaiseAfterMs: number;
  readonly sinceTierRaiseMs: number | null;
  /** Consecutive frames longer than `crawlFrameMs` so far (see `crawl`). */
  readonly crawlFrames: number;
  /** Consecutive frames between `maxFrameMs` and `crawlFrameMs` so far (#501). */
  readonly slowRun: number;
  /**
   * This window already set aside a hitch (a run of frames over
   * `maxFrameMs`): another run counts at its real length (#501 G), so slow
   * frames broken up by quick ones (33/150/33/150, about 11 fps) still read
   * as slow.
   */
  readonly windowHitch: boolean;
}

export interface GovernorContext {
  readonly config: ScalerConfig;
  readonly devicePixelRatio: number;
}

export function initialGovernor(tier: QualityTier, config: ScalerConfig): GovernorState {
  return {
    tier,
    ceiling: tier,
    renderScale: 1,
    elapsedMs: 0,
    windowMs: 0,
    windowFrames: 0,
    settleWindows: 0,
    goodMs: 0,
    raiseAfterMs: config.raiseAfterMs,
    sinceRaiseMs: null,
    probe: null,
    tierProbe: null,
    cap: null,
    capHoldMs: config.capHoldMs,
    tierProbeAfterMs: 0,
    slowAtFloorMs: 0,
    tierGoodMs: 0,
    tierRaiseAfterMs: config.tierRaiseAfterMs,
    sinceTierRaiseMs: null,
    crawlFrames: 0,
    slowRun: 0,
    windowHitch: false,
  };
}

/** Rounded so repeated steps don't accumulate float noise (0.7500000001). */
function roundScale(scale: number): number {
  return Math.round(scale * 1000) / 1000;
}

const plus = (t: number | null, ms: number): number | null => (t === null ? null : t + ms);

export function stepGovernor(
  state: GovernorState,
  frameMs: number,
  ctx: GovernorContext,
): GovernorState {
  const { config } = ctx;
  if (!(frameMs > 0)) return state;
  if (frameMs > config.crawlFrameMs) return crawl(state, ctx);
  // Any shorter frame ends a run of crawling frames (they must be consecutive).
  if (state.crawlFrames > 0) state = { ...state, crawlFrames: 0 };
  // A slow frame short of crawling: one or two (a shader compile, a hitch)
  // say nothing about the frame rate, but a run of them is the frame rate
  // (#501: a phone stuck at 2–3 fps draws every frame in this band, and
  // ignoring them all left no window to close, so nothing ever stepped down).
  // From the run's `crawlFrames`-th frame they count at their real length, so
  // a cut that helps but still leaves frames this slow reads as helping. A
  // crawling frame between them leaves the run going: both say "slow".
  // Only one such hitch (one run) a window is set aside: a second run in
  // the same window counts (#501 G), however the quick frames between broke it.
  if (frameMs > config.maxFrameMs) {
    const slowRun = state.slowRun + 1;
    const sameHitch = state.slowRun > 0 || !state.windowHitch;
    if (slowRun < config.crawlFrames && sameHitch) {
      return { ...state, slowRun, windowHitch: true };
    }
    state = { ...state, slowRun };
  } else if (state.slowRun > 0) {
    state = { ...state, slowRun: 0 };
  }

  const elapsedMs = state.elapsedMs + frameMs;
  if (elapsedMs < config.graceMs) return { ...state, elapsedMs };

  const windowMs = state.windowMs + frameMs;
  const windowFrames = state.windowFrames + 1;
  const next = {
    ...state,
    elapsedMs,
    sinceRaiseMs: plus(state.sinceRaiseMs, frameMs),
    sinceTierRaiseMs: plus(state.sinceTierRaiseMs, frameMs),
  };
  if (windowMs < config.windowMs) return { ...next, windowMs, windowFrames };

  const closed = { ...next, windowMs: 0, windowFrames: 0, windowHitch: false };
  if (state.settleWindows > 0) return { ...closed, settleWindows: state.settleWindows - 1 };
  return decide(closed, (windowFrames * 1000) / windowMs, windowMs, ctx);
}

/**
 * The page paused (a hidden tab, an iOS app switch, a device asleep and
 * back): the frame that spans it is a gap, never a crawling frame, however
 * many gaps come around one pause. Called on `visibilitychange` and
 * `pageshow`; a normal frame ends a run as well.
 */
export function resumeGovernor(state: GovernorState): GovernorState {
  return state.crawlFrames === 0 && state.slowRun === 0
    ? state
    : { ...state, crawlFrames: 0, slowRun: 0 };
}

/**
 * Another frame longer than `crawlFrameMs`. Once `crawlFrames` of them come in
 * a row the renderer is crawling (a very weak GPU, software WebGL in CI):
 * every frame is "too long to count", so the windows above never close and
 * nothing would ever change. Go straight to the cheapest tier at one render
 * pixel per CSS pixel (a tier at a time would mean a shader rebuild, and more
 * crawling frames, per step); the usual raises bring quality back if the
 * slowness passes.
 */
function crawl(s: GovernorState, ctx: GovernorContext): GovernorState {
  const { config, devicePixelRatio } = ctx;
  const crawlFrames = s.crawlFrames + 1;
  if (crawlFrames < config.crawlFrames) return { ...s, crawlFrames };
  let tier = s.tier;
  for (let cheaper = lowerTier(tier); cheaper !== null; cheaper = lowerTier(cheaper)) {
    tier = cheaper;
  }
  const renderScale = roundScale(crawlRenderScale(devicePixelRatio));
  if (tier === s.tier && renderScale === s.renderScale) return { ...s, crawlFrames: 0 };
  return {
    ...s,
    tier,
    renderScale,
    crawlFrames: 0,
    settleWindows: config.settleWindows,
    probe: null,
    tierProbe: null,
    cap: null,
    goodMs: 0,
    tierGoodMs: 0,
    slowAtFloorMs: 0,
    sinceRaiseMs: null,
    sinceTierRaiseMs: null,
  };
}

/** Undo a step that didn't help and hold at this frame rate. */
function holdAtCap(
  s: GovernorState,
  fps: number,
  undo: { renderScale: number; tier: QualityTier },
  config: ScalerConfig,
): GovernorState {
  const changed = undo.renderScale !== s.renderScale || undo.tier !== s.tier;
  return {
    ...s,
    ...undo,
    settleWindows: changed ? config.settleWindows : 0,
    probe: null,
    tierProbe: null,
    cap: { fps, heldMs: 0, holdMs: s.capHoldMs },
    capHoldMs: Math.min(s.capHoldMs * 2, config.maxCapHoldMs),
    slowAtFloorMs: 0,
    goodMs: 0,
    tierGoodMs: 0,
  };
}

function decide(s: GovernorState, fps: number, w: number, ctx: GovernorContext): GovernorState {
  const { config, devicePixelRatio } = ctx;
  if (s.cap) {
    const heldMs = s.cap.heldMs + w;
    if (fps >= config.raiseAtFps) {
      s = { ...s, cap: null, capHoldMs: config.capHoldMs }; // the cap really lifted
    } else if (fps < s.cap.fps * (1 - config.capTolerance) || heldMs >= s.cap.holdMs) {
      s = { ...s, cap: null }; // new load, or time to re-test
    } else {
      return { ...s, cap: { ...s.cap, heldMs }, goodMs: 0, tierGoodMs: 0 };
    }
  }

  if (s.tierProbe) {
    const { baselineFps, tierBefore } = s.tierProbe;
    if (fps < config.lowerBelowFps && fps < baselineFps * config.cutGain) {
      const renderScale = roundScale(
        Math.max(renderScaleFloor(tierBefore, devicePixelRatio), s.renderScale),
      );
      // The tier isn't the cost either: don't probe it again for a while.
      const failed = { ...s, tierProbeAfterMs: s.elapsedMs + config.tierProbeCooldownMs };
      return holdAtCap(
        failed,
        Math.max(fps, baselineFps),
        { renderScale, tier: tierBefore },
        config,
      );
    }
    s = { ...s, tierProbe: null };
  }

  if (fps < config.lowerBelowFps) return slow(s, fps, w, ctx);

  if (fps < config.raiseAtFps) {
    // Close enough: stop cutting, but this isn't headroom either.
    return { ...s, probe: null, goodMs: 0, tierGoodMs: 0, slowAtFloorMs: 0 };
  }
  return good(s, w, config);
}

function slow(s: GovernorState, fps: number, w: number, ctx: GovernorContext): GovernorState {
  const { config, devicePixelRatio } = ctx;
  if (s.probe && fps < s.probe.baselineFps * config.cutGain) {
    // That cut didn't help: pixels aren't the cost. Far below target, the
    // tier's effects may be (#501: 28 fps on high with a team at the lake,
    // held at full resolution): put the pixels back and try the next tier
    // down. Its probe puts the tier back too if that doesn't help either, so
    // a real cap (Low Power Mode's 30 fps) costs one reverted probe per re-test.
    const cheaper = tierToProbe(s);
    if (fps < config.tierProbeBelowFps && cheaper !== null) {
      return dropTier(
        { ...s, probe: null, goodMs: 0, tierGoodMs: 0, renderScale: s.probe.scaleBefore },
        Math.max(fps, s.probe.baselineFps),
        cheaper,
        ctx,
      );
    }
    // Capped or CPU-bound: put the pixels back and hold.
    return holdAtCap(
      s,
      Math.max(fps, s.probe.baselineFps),
      { renderScale: s.probe.scaleBefore, tier: s.tier },
      config,
    );
  }

  const floor = renderScaleFloor(s.tier, devicePixelRatio);
  const base = { ...s, probe: null, goodMs: 0, tierGoodMs: 0 };

  if (s.renderScale > floor) {
    // One decisive cut, at least `minCutStep`: every new scale reallocates
    // the post-process targets, so many small cuts cost a stall each (#501).
    // Right after a raise the scale before it was close: the cut is at
    // least one `step` (as before #501), not a whole `minCutStep`, so it
    // never lands below what the shortfall asks for.
    const flapped = s.sinceRaiseMs !== null && s.sinceRaiseMs <= config.flapWindowMs;
    const wanted = s.renderScale * Math.sqrt(fps / config.targetFps);
    const minCut = flapped ? config.step : config.minCutStep;
    const renderScale = roundScale(Math.max(floor, Math.min(wanted, s.renderScale - minCut)));
    return {
      ...base,
      renderScale,
      settleWindows: config.settleWindows,
      probe: { baselineFps: fps, scaleBefore: s.renderScale },
      slowAtFloorMs: 0,
      sinceRaiseMs: null,
      raiseAfterMs: flapped ? Math.min(s.raiseAfterMs * 2, config.maxRaiseAfterMs) : s.raiseAfterMs,
    };
  }

  // At the floor, and the last cut (if any) helped. Nothing here changes
  // the resolution, so a cap re-test at the floor only measures: the one
  // change it can make is a tier probe, and not within the cooldown (#501).
  if (fps >= config.tierDropBelowFps) return { ...base, slowAtFloorMs: 0 };
  const slowAtFloorMs = s.slowAtFloorMs + w;
  const cheaper = tierToProbe(s);
  if (slowAtFloorMs < config.tierDropAfterMs || cheaper === null) {
    return { ...base, slowAtFloorMs };
  }
  return dropTier(base, fps, cheaper, ctx);
}

/** The next tier down to probe, or null: on the cheapest, or a probe failed too recently. */
function tierToProbe(s: GovernorState): QualityTier | null {
  return s.elapsedMs < s.tierProbeAfterMs ? null : lowerTier(s.tier);
}

/** Steps down to `cheaper` as a probe: the next window tells whether it helped. */
function dropTier(
  s: GovernorState,
  fps: number,
  cheaper: QualityTier,
  ctx: GovernorContext,
): GovernorState {
  const { config, devicePixelRatio } = ctx;
  const flapped = s.sinceTierRaiseMs !== null && s.sinceTierRaiseMs <= config.tierFlapWindowMs;
  return {
    ...s,
    tier: cheaper,
    renderScale: roundScale(Math.max(renderScaleFloor(cheaper, devicePixelRatio), s.renderScale)),
    settleWindows: config.settleWindows,
    tierProbe: { baselineFps: fps, tierBefore: s.tier },
    slowAtFloorMs: 0,
    sinceTierRaiseMs: null,
    tierRaiseAfterMs: flapped
      ? Math.min(s.tierRaiseAfterMs * 2, config.maxTierRaiseAfterMs)
      : s.tierRaiseAfterMs,
  };
}

function good(s: GovernorState, w: number, config: ScalerConfig): GovernorState {
  const base = { ...s, probe: null, slowAtFloorMs: 0 };

  if (s.renderScale < 1) {
    const goodMs = s.goodMs + w;
    if (goodMs < s.raiseAfterMs) return { ...base, goodMs, tierGoodMs: 0 };
    return {
      ...base,
      renderScale: roundScale(Math.min(1, s.renderScale + config.step)),
      settleWindows: config.settleWindows,
      goodMs: 0,
      tierGoodMs: 0,
      sinceRaiseMs: 0,
    };
  }

  const tierGoodMs = s.tierGoodMs + w;
  const better = higherTier(s.tier, s.ceiling);
  if (better === null || tierGoodMs < s.tierRaiseAfterMs) {
    return { ...base, goodMs: 0, tierGoodMs };
  }
  return {
    ...base,
    tier: better,
    settleWindows: config.settleWindows,
    goodMs: 0,
    tierGoodMs: 0,
    sinceTierRaiseMs: 0,
  };
}

/**
 * A burst of work is about to land (a find: the scene update, the Keeper's
 * cheer, the card or the flying finds; #501 G). The frames around it say
 * nothing about the GPU, so the current window is thrown away and the next
 * `settleWindows` are too, as after a change. Nothing is applied, so this
 * costs no reallocation.
 */
export function expectHitch(state: GovernorState, config: ScalerConfig): GovernorState {
  return {
    ...state,
    windowMs: 0,
    windowFrames: 0,
    windowHitch: false,
    settleWindows: Math.max(state.settleWindows, config.settleWindows),
  };
}

/**
 * The governor's settings for a loop capped at `fps` (`FrameCap`): every
 * frame-rate threshold scaled to the cap, so the cap reads as on target, not
 * as a GPU that can't keep up (no cuts, no tier probes). The longest frame a
 * window takes on its own shrinks to `cappedHitchFrames` periods: at 250 ms,
 * one 200 ms hitch among 33 ms frames read as 20 fps and cut the resolution,
 * whose reallocation was the next hitch (#501 G). A run of slower frames
 * still counts at real length (`stepGovernor`).
 */
export function cappedScaler(config: ScalerConfig, fps: number): ScalerConfig {
  const k = fps / config.targetFps;
  return {
    ...config,
    targetFps: fps,
    maxFrameMs: Math.min(config.maxFrameMs, (config.cappedHitchFrames * 1000) / fps),
    lowerBelowFps: config.lowerBelowFps * k,
    raiseAtFps: config.raiseAtFps * k,
    tierProbeBelowFps: config.tierProbeBelowFps * k,
    tierDropBelowFps: config.tierDropBelowFps * k,
  };
}
